import { ColoursObj, Typography, getContrastYIQ } from "@eventr/common-storybook";
import { Box } from "@mui/material";
import { useEffect, useRef, useState } from "react";

const TITLE_WIDTH = "calc(var(--app-width) - 86px)";

// The one-line and two-line renderings of the title. Everything except the metrics is
// shared, so the hidden probe below measures the same glyphs as the visible text.
const TITLE_FONT = {
  fontWeight: "700",
  fontFamily: "Inter-600-900",
} as const;
const ONE_LINE_METRICS = { fontSize: "20px", lineHeight: "32px" } as const;
const TWO_LINE_METRICS = { fontSize: "14px", lineHeight: "16px" } as const;

export function ItineraryTitle({ id, name }: { id?: string; name: string }) {
  const [useLongVer, setUseLongVer] = useState<boolean>(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const probeRef = useRef<HTMLSpanElement>(null);

  // The title is drawn large on one line when it fits and drops to the smaller two-line
  // style when it doesn't. The decision is measured against the hidden single-line probe
  // at the large size, not against the visible text: measuring the visible text feeds
  // back on itself, because the shrunken two-line version always "fits" again.
  useEffect(() => {
    const container = containerRef.current;
    const probe = probeRef.current;
    if (!container || !probe) return;

    const measure = () => {
      const available = container.getBoundingClientRect().width;
      // On device the carousel is laid out before its webview has a width, so every
      // measurement reads 0. The old check treated that as "the text filled the line"
      // and latched the two-line style on for good; wait for a real width instead.
      if (available === 0) return;
      setUseLongVer(probe.getBoundingClientRect().width > available);
    };

    measure();

    // Re-measure when the webview finally gets a width, on rotation, and when the custom
    // font finishes loading (which changes the probe's width).
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    observer.observe(probe);
    return () => observer.disconnect();
  }, [name]);

  return (
    <Box
      ref={containerRef}
      sx={{
        display: "grid",
        justifySelf: "center",
        justifyItems: "center",
        maxHeight: "44px",
        width: TITLE_WIDTH,
        overflow: "hidden",
      }}
    >
      <Typography
        sx={{
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          textOverflow: "ellipsis",
          overflow: "hidden",
          display: "-webkit-box",
          alignItems: "center",
          textAlign: "center",
          width: "max-content",
          maxWidth: TITLE_WIDTH,
        }}
        color={getContrastYIQ(ColoursObj.backgrounds.header)}
        style={{
          ...TITLE_FONT,
          ...(useLongVer ? TWO_LINE_METRICS : ONE_LINE_METRICS),
          zIndex: 100,
        }}
        id={`${id}itineraryName`}
      >
        {name}
      </Typography>
      {/* Out-of-flow single-line copy at the one-line size, used only for measurement. */}
      <span
        ref={probeRef}
        aria-hidden
        style={{
          position: "absolute",
          visibility: "hidden",
          pointerEvents: "none",
          width: "max-content",
          whiteSpace: "nowrap",
        }}
      >
        <Typography style={{ ...TITLE_FONT, ...ONE_LINE_METRICS }}>{name}</Typography>
      </span>
    </Box>
  );
}
